import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type CreateForwardedInvoicePreviewPayload,
  type ForwardedInvoicePreviewResponse,
} from './types';
import { zodCreateForwardedInvoicePreviewSchema } from './validations';

/**
 * Creates a preview of a reprocessing invoice with the given process ID that the user wants to forward.
 * @param payload - The payload containing the process ID, company ID, and reason code.
 * @returns The preview data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/create_invoice_preview_invoice_process__process_id__invoice_preview_post
 */
export const createForwardedInvoicePreview = async (
  payload: CreateForwardedInvoicePreviewPayload,
) => {
  try {
    const validatedPayload =
      zodCreateForwardedInvoicePreviewSchema.parse(payload);

    const response = await vestigasApi
      .post(
        ENDPOINT.INVOICE_REPROCESSING.CREATE_FORWARDED_INVOICE_PREVIEW(
          validatedPayload.processId,
        ),
        {
          json: {
            companyId: validatedPayload.companyId,
            reasonCode: validatedPayload.reasonCode,
          },
        },
      )
      .json<ForwardedInvoicePreviewResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error creating forwarded invoice preview',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(
            ($) => $.createForwardedInvoicePreview.error422,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.createForwardedInvoicePreview.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
